import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Autocomplete,
  Divider,
  Tab,
  Tabs,
} from "@mui/material";
import {
  getConsumoMetrics,
  type TConsumoResponse,
  type TSupplierData,
} from "@/api/customs/consumo";
import { PivotTableActiveUsers } from "./PivotTableActiveUsers";
import { format } from "date-fns";

export const DashboardConsumo = () => {
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(1); // Primer día del mes actual
    return format(date, "yyyy-MM-dd");
  };

  const [startDate, setStartDate] = useState<string>(getDefaultStartDate());
  const [endDate, setEndDate] = useState<string>(format(new Date(), "yyyy-MM-dd"));
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TConsumoResponse | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const fetchData = async () => {
    if (!startDate || !endDate) {
      setError("Por favor selecciona ambas fechas");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getConsumoMetrics({
        startDate,
        endDate,
      });
      setData(response);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Error al cargar los datos"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getAvailableSuppliers = () => {
    if (!data) return [];
    return Object.entries(data).map(([supplierId, supplierData]) => ({
      id: supplierId,
      name: supplierData.supplier_name,
    }));
  };

  const getFilteredSuppliers = (): Array<{ id: string; data: TSupplierData }> => {
    if (!data) return [];

    const suppliersToProcess =
      selectedSupplierIds.length === 0
        ? Object.entries(data)
        : Object.entries(data).filter(([id]) =>
            selectedSupplierIds.includes(id)
          );

    return suppliersToProcess.map(([id, supplierData]) => ({
      id,
      data: supplierData,
    }));
  };

  const availableSuppliers = getAvailableSuppliers();
  const filteredSuppliers = getFilteredSuppliers();
  
  const grandTotal = filteredSuppliers.reduce(
    (sum, s) => sum + s.data.total_cost,
    0
  );
  const totalConversations = filteredSuppliers.reduce(
    (sum, s) => sum + s.data.total_conversations,
    0
  );
  const totalTokens = filteredSuppliers.reduce(
    (sum, s) => sum + s.data.total_tokens,
    0
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard Consumo
      </Typography>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
        >
          <Tab label="Consumo" />
          <Tab label="Active Users" />
        </Tabs>
      </Box>

      {/* Tab 0: Consumo (original content with its own filters) */}
      {activeTab === 0 && (
        <>
          {/* Filtros */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Fecha Inicio"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Fecha Fin"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    multiple
                    options={availableSuppliers}
                    getOptionLabel={(option) => option.name}
                    value={availableSuppliers.filter((supplier) =>
                      selectedSupplierIds.includes(supplier.id)
                    )}
                    onChange={(_, newValue) => {
                      setSelectedSupplierIds(newValue.map((s) => s.id));
                    }}
                    disabled={!data || loading}
                    renderInput={(params) => (
                      <TextField {...params} label="Proveedores" />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.name}
                          {...getTagProps({ index })}
                          key={option.id}
                        />
                      ))
                    }
                    filterSelectedOptions
                    noOptionsText="No hay proveedores disponibles"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button
                    variant="contained"
                    onClick={fetchData}
                    disabled={loading || !startDate || !endDate}
                    fullWidth
                  >
                    {loading ? <CircularProgress size={24} /> : "Consultar"}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {data && !loading && (
            <>
              {/* Resumen General */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Resumen General
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="h4" color="primary">
                          ${grandTotal.toFixed(4)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Costo Total
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="h4" color="primary">
                          {filteredSuppliers.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Proveedores
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="h4" color="primary">
                          {totalConversations}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Conversaciones
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="h4" color="primary">
                          {totalTokens.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Tokens Totales
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Tarjetas por Proveedor */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Detalle por Proveedor ({filteredSuppliers.length})
                </Typography>
                <Grid container spacing={2}>
                  {filteredSuppliers.map(({ id, data: supplierData }) => (
                    <Grid item xs={12} md={6} key={id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom color="primary">
                            {supplierData.supplier_name}
                          </Typography>
                          
                          {/* Rango de Fechas */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Período: {startDate} - {endDate}
                            </Typography>
                          </Box>

                          {/* Agentes */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Agentes:
                            </Typography>
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                              {supplierData.agents.map((agent, idx) => (
                                <Chip key={idx} label={agent} size="small" color="info" />
                              ))}
                            </Box>
                          </Box>

                          <Grid container spacing={2}>
                            {/* Costos Mensajería */}
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" fontWeight="bold">
                                Costos Mensajería
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Costo Mensajería para Cliente:
                              </Typography>
                              <Typography variant="body1">
                                ${supplierData.marketing_cost.toFixed(4)}
                              </Typography>
                            </Grid>

                            {/* Métricas Marketing */}
                            <Grid item xs={12} sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary" fontWeight="bold">
                                Métricas
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Respuestas Marketing:
                              </Typography>
                              <Typography variant="body1">
                                {supplierData.marketing_responses}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Éxitos Marketing:
                              </Typography>
                              <Typography variant="body1">
                                {supplierData.marketing_success}
                              </Typography>
                            </Grid>

                            {/* Divider */}
                            <Grid item xs={12}>
                              <Divider sx={{ my: 1 }} />
                            </Grid>

                            {/* Costos AI */}
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" fontWeight="bold">
                                Costos AI
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Costo Operativo OpenAI:
                              </Typography>
                              <Typography variant="body1">
                                ${supplierData.ai_cost.toFixed(4)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Costo Input:
                              </Typography>
                              <Typography variant="body1">
                                ${supplierData.total_input_cost.toFixed(4)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Costo Output:
                              </Typography>
                              <Typography variant="body1">
                                ${supplierData.total_output_cost.toFixed(4)}
                              </Typography>
                            </Grid>

                            {/* Tokens */}
                            <Grid item xs={12} sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary" fontWeight="bold">
                                Tokens
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" color="text.secondary">
                                Total:
                              </Typography>
                              <Typography variant="body1">
                                {supplierData.total_tokens.toLocaleString()}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" color="text.secondary">
                                Input:
                              </Typography>
                              <Typography variant="body1">
                                {supplierData.total_input_tokens.toLocaleString()}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" color="text.secondary">
                                Output:
                              </Typography>
                              <Typography variant="body1">
                                {supplierData.total_output_tokens.toLocaleString()}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </>
          )}
        </>
      )}

      {/* Tab 1: Active Users (self-contained with its own month filter) */}
      {activeTab === 1 && <PivotTableActiveUsers />}
    </Box>
  );
};

